import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { readFragment, type ResultOf } from "gql.tada";
import { useStore } from "@/app/store";
import type { DimensionEditState } from "../components/CostPanel/CostPanel.types";
import type { CalculationCategoryResult } from "./useCalculationDetail";
import {
  CALCULATION_DETAIL_FRAGMENT,
  GET_DIMENSION_TYPES,
} from "../graphql/queries";
import { UPDATE_DIMENSION_VALUE_SUBSET } from "../graphql/mutations";
import useStaleVersionHandler from "./useStaleVersionHandler";

type DimensionType = ResultOf<
  typeof GET_DIMENSION_TYPES
>["dimensionTypes"][number];

interface UseEditDimensionValuesArgs {
  activeCategory: CalculationCategoryResult | undefined;
  panelActive: boolean;
  onConflict: () => void;
  onSuccess: () => void;
  onError: () => void;
  onPendingActiveDimensionValue: (value: string | null) => void;
}

const useEditDimensionValues = ({
  activeCategory,
  panelActive,
  onConflict,
  onSuccess,
  onError,
  onPendingActiveDimensionValue,
}: UseEditDimensionValuesArgs) => {
  const setCalculation = useStore((s) => s.setCalculation);
  const handleStale = useStaleVersionHandler(onConflict);

  const { data: dimensionTypesData } = useQuery(GET_DIMENSION_TYPES, {
    skip: !panelActive,
  });

  const [runMutation, { loading: isSubmitting }] = useMutation(
    UPDATE_DIMENSION_VALUE_SUBSET,
  );

  const dimensionEditState: DimensionEditState | null = useMemo(() => {
    if (!activeCategory?.dimensionTypeId) return null;
    const dimType: DimensionType | undefined =
      dimensionTypesData?.dimensionTypes.find(
        (t) => t.id === activeCategory.dimensionTypeId,
      );
    if (!dimType) return null;
    return {
      dimensionTypeName: activeCategory.dimensionTypeName ?? dimType.name,
      seededValues: dimType.values,
    };
  }, [activeCategory, dimensionTypesData]);

  const enter = useCallback(() => {
    if (!activeCategory?.dimensionTypeId) return false;
    return true;
  }, [activeCategory]);

  const submit = useCallback(
    async (values: string[]) => {
      if (!activeCategory) return;
      const currentSet = new Set(activeCategory.dimensionValues);
      const firstNew = values.find((v) => !currentSet.has(v)) ?? null;
      if (firstNew) onPendingActiveDimensionValue(firstNew);
      const { calculation } = useStore.getState();
      if (!calculation) return;
      const calledForId = calculation.id;
      const result = await runMutation({
        variables: {
          input: {
            calculationId: calledForId,
            subCategoryId: activeCategory.subCategoryDefinitionId,
            dimensionValues: values,
            expectedVersion: calculation.version,
          },
        },
      });
      if (handleStale(result.error)) return;
      if (result.error) {
        onError();
        return;
      }
      if (result.data?.updateDimensionValueSubset) {
        const wrote = setCalculation(
          readFragment(
            CALCULATION_DETAIL_FRAGMENT,
            result.data.updateDimensionValueSubset,
          ),
          calledForId,
        );
        if (wrote) onSuccess();
      }
    },
    [
      activeCategory,
      handleStale,
      onError,
      onPendingActiveDimensionValue,
      onSuccess,
      runMutation,
      setCalculation,
    ],
  );

  return {
    dimensionEditState,
    isSubmitting,
    enter,
    submit,
  };
};

export default useEditDimensionValues;
